import { authentication, useAuthentication } from '@ecosystem/newron-client-lib';
import axios from 'axios';
import { useCallback, useState } from 'react';

import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { byFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import { FilterKeys, PageType } from '@utils/pageConfig/Types';
import { cloneDeep } from 'lodash';
import { getItemGroupByProductGroup } from '@services/ItemGroup.service';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { shareEnvironment } from '@environments/environment';
import { IMPERSONATION_STORAGE_KEY } from '@utils/impersonateUtils/ImpersonationUtil';

interface Market {
  country: string;
  itemGroup: string;
  itemGroupCode: string;
  countryCode: string;
}

export interface UserList {
  sameAccessUsers: UserToShare[];
  otherClientUsers: UserToShare[];
}

export interface UserToShare {
  userId: string;
  givenName?: string;
  familyName?: string;
  email: string;
  country?: string;
}

interface SharingUserGroupsResponse {
  user_group_id: number;
  user_group_name: string;
  client_id: number;
  users_count: number;
}

export interface SharingUserGroup {
  userGroupId: number;
  userGroupName: string;
  clientId: number;
  usersCount: number;
}

export const useSharingConfig = (): {
  marketDescription: string;
  market: Market;
  getUsers: () => Promise<any[] | UserToShare[]>;
  getUsersForGrouping: () => Promise<UserToShare[]>;
  getUserGroupsForGrouping: () => Promise<SharingUserGroup[]>;
} => {
  const { getAccessToken } = useAuthentication();
  const pagesConfig = usePageConfig();

  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const mainFilterStateProducts: MainFilterStateProducts = useSelector(
    (state: IRootState) => state.mainFiltersProducts,
  );
  const [marketDescription, setMarketDescription] = useState<string>('');
  const [market, setMarket] = useState<Market>({
    country: '',
    countryCode: '',
    itemGroup: '',
    itemGroupCode: '',
  });
  const selectedFilters = pagesConfig.savedFilterToShare.filters;

  const getUsersToShare = useCallback(async () => {
    if (!selectedFilters || selectedFilters.length === 0) {
      return [];
    }
    const appliedFiltersCountry = cloneDeep([...selectedFilters].find(byFilterKey(FilterKeys.COUNTRY))!).options[0];

    const appliedPg = cloneDeep([...selectedFilters].find(byFilterKey(FilterKeys.PRODUCT_GROUP)))?.options[0];
    if (!appliedPg) {
      return [];
    }

    const mainFilterTimeFrame = mainFiltersState.timeFrame ?? '';

    const itemGroup = await getItemGroupByProductGroup(
      pagesConfig.clientId,
      pagesConfig.pageType !== PageType.RI_PRODUCT
        ? ProductDataType.PerformanceAnalyzer
        : ProductDataType.ProductAnalyzer,
      [appliedFiltersCountry.value],
      pagesConfig.pageType !== PageType.RI_PRODUCT ? mainFilterTimeFrame : mainFilterStateProducts.timeFrame,
      [appliedPg.value],
    );

    setMarket({
      country: appliedFiltersCountry.label,
      countryCode: appliedFiltersCountry.value,
      itemGroup: itemGroup[0] ? itemGroup[0] : '',
      itemGroupCode: itemGroup[0] ? itemGroup[0] : '',
    });
    setMarketDescription(`${itemGroup[0]} in ${appliedFiltersCountry.label}`);

    //# the product might need to do convertion to the itemGroup in newron.
    return getUserByAccess();
  }, [getAccessToken, selectedFilters]);

  const getUserByAccess = useCallback(async () => {
    const owner = authentication.getUser();
    const token = await getAccessToken();
    const marketUsersResponse = await axios.get<UserList>(`${shareEnvironment.baseUrl}/users/users-by-access`, {
      params: { filterInternal: 'true' },
      headers: { Authorization: `Bearer ${token}` },
    });

    let usersToShare;
    if (owner?.isInternalUser) {
      usersToShare = [
        ...marketUsersResponse.data.otherClientUsers.filter((x) => !!x.email),
        ...marketUsersResponse.data.sameAccessUsers.filter((x) => !!x.email),
      ];
    } else {
      usersToShare = marketUsersResponse.data.sameAccessUsers.filter((x) => !!x.email);
    }

    return usersToShare;
  }, [getAccessToken]);

  const getSharingUserGroups = useCallback(async () => {
    const token = await getAccessToken();
    const storage = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    const impersonatedClientId = storage ? JSON.parse(storage).client_id : null;

    if (!impersonatedClientId) {
      return [];
    }

    const result = await axios.get<SharingUserGroupsResponse[]>(`${shareEnvironment.personalUrl}/sharing/user-groups`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { clientId: impersonatedClientId },
      paramsSerializer: { indexes: null },
    });

    const mapToUserGroup = (userGroup: SharingUserGroupsResponse): SharingUserGroup => ({
      userGroupId: userGroup.user_group_id,
      userGroupName: userGroup.user_group_name,
      clientId: userGroup.client_id,
      usersCount: userGroup.users_count,
    });

    return result.data.map(mapToUserGroup);
  }, [getAccessToken]);

  return {
    marketDescription,
    getUsers: getUsersToShare,
    getUsersForGrouping: getUserByAccess,
    getUserGroupsForGrouping: getSharingUserGroups,
    market,
  };
};
